import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import { 
  ShieldCheck, 
  Banknote, 
  Globe, 
  Zap, 
  MessageCircle,
  CheckCircle,
  ArrowRight,
  Clock,
  Users,
  Award,
  Star,
  X,
  MapPin,
  ChevronDown,
  ChevronUp,
  Flame,
  Gift,
  AlertCircle
} from "lucide-react";
const BannersPage = lazy(() => import("./Banners"));

// Remove Emergent badge on load
if (typeof window !== 'undefined') {
  const removeBadge = () => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    document.querySelectorAll('a[href*="emergent.sh"]').forEach(el => el.style.display = 'none');
  };
  removeBadge();
  setTimeout(removeBadge, 0);
  setTimeout(removeBadge, 100);
  setTimeout(removeBadge, 300);
  setTimeout(removeBadge, 500);
  setTimeout(removeBadge, 1000);
  document.addEventListener('DOMContentLoaded', removeBadge);
  window.addEventListener('load', removeBadge);
  
  // MutationObserver to catch dynamically added badge
  const observer = new MutationObserver(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

const TELEGRAM_URL = "https://t.me/Googleadsenseearning";

const handleTelegramClick = (e) => {
  e.preventDefault();
  if (typeof window.gtag_report_conversion === 'function') {
    window.gtag_report_conversion(TELEGRAM_URL);
  } else {
    window.location.href = TELEGRAM_URL;
  }
};

// Region-specific avatars - unique faces for each region
const regionAvatars = {
  africa: [
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1463453091185-61582044d556?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1489980557514-251d61e3eeb6?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1506277886164-e25aa3f4ef7f?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1504257432389-52343af06ae3?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=48&h=48&fit=crop&crop=face"
  ],
  asia: [
    "https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1513910367299-bce8d8a0ebf6?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1542909168-82c3e7fdca5c?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1544168190-79c17527004f?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=48&h=48&fit=crop&crop=face"
  ],
  europe: [
    "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=48&h=48&fit=crop&crop=face"
  ],
  uk: [
    "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=48&h=48&fit=crop&crop=face"
  ],
  usa: [
    "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1463453091185-61582044d556?w=48&h=48&fit=crop&crop=face"
  ],
  middleeast: [
    "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1507591064344-4c6ce005b128?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1492562080023-ab3db95bfbce?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1566492031773-4f4e44671857?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1542909168-82c3e7fdca5c?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1504257432389-52343af06ae3?w=48&h=48&fit=crop&crop=face"
  ],
  latam: [
    "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=48&h=48&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=48&h=48&fit=crop&crop=face"
  ]
};

// Country-specific names that match the culture
const countryNames = {
  // Africa
  "Nigeria": { first: ["Chukwuemeka", "Oluwaseun", "Adaeze", "Chidinma", "Emeka", "Ngozi", "Tunde", "Funke", "Segun", "Yetunde", "Olumide", "Bukola", "Chidi", "Amaka", "Femi", "Bisi"], last: ["O", "A", "E", "I", "U", "N", "C", "K", "B", "D"] },
  "Ghana": { first: ["Kwame", "Kofi", "Ama", "Akua", "Yaw", "Adjoa", "Kwesi", "Abena", "Kojo", "Efua", "Kwaku", "Afia", "Nana", "Akosua"], last: ["A", "O", "M", "K", "B", "D", "S", "N"] },
  "Kenya": { first: ["Wanjiku", "Kamau", "Njeri", "Mwangi", "Akinyi", "Odhiambo", "Wambui", "Kariuki", "Nyambura", "Otieno", "Wairimu", "Kimani"], last: ["K", "M", "W", "O", "N", "G", "A"] },
  "South Africa": { first: ["Thabo", "Sipho", "Nomvula", "Lindiwe", "Bongani", "Zanele", "Mandla", "Nkosazana", "Sibusiso", "Thandiwe", "Dumisani", "Ayanda"], last: ["M", "N", "D", "S", "K", "Z", "B"] },
  "Senegal": { first: ["Mamadou", "Fatou", "Ousmane", "Aminata", "Ibrahima", "Mariama", "Moussa", "Aissatou", "Abdoulaye", "Khady", "Cheikh", "Ndeye"], last: ["D", "S", "N", "F", "B", "K", "M"] },
  "Cameroon": { first: ["Jean-Pierre", "Marie-Claire", "Emmanuel", "Sandrine", "Patrick", "Christelle", "Rodrigue", "Yvette", "Blaise", "Nadege"], last: ["N", "T", "M", "B", "F", "K", "E"] },
  "Tanzania": { first: ["Juma", "Amina", "Hassan", "Rehema", "Bakari", "Zainab", "Rashid", "Halima", "Selemani", "Mwanaisha"], last: ["M", "H", "A", "S", "K", "J", "R"] },
  "Uganda": { first: ["Ronald", "Esther", "Moses", "Sarah", "Samuel", "Grace", "Patrick", "Florence", "David", "Rose"], last: ["K", "M", "O", "N", "S", "B", "A"] },
  "Zimbabwe": { first: ["Tendai", "Rumbidzai", "Tapiwa", "Chiedza", "Tatenda", "Rutendo", "Tanaka", "Rudo", "Kudakwashe", "Nyasha"], last: ["M", "N", "C", "Z", "D", "G", "K"] },
  "Ethiopia": { first: ["Abebe", "Tigist", "Dawit", "Meron", "Yonas", "Hanna", "Solomon", "Bethlehem", "Tesfaye", "Sara"], last: ["T", "G", "M", "A", "H", "B", "K"] },
  
  // Asia
  "India": { first: ["Rahul", "Priya", "Amit", "Sneha", "Vikram", "Anjali", "Rajesh", "Pooja", "Suresh", "Deepika", "Arun", "Kavita", "Sanjay", "Neha"], last: ["S", "P", "K", "M", "R", "G", "V", "A"] },
  "Pakistan": { first: ["Ahmed", "Fatima", "Muhammad", "Ayesha", "Ali", "Sana", "Hassan", "Zainab", "Omar", "Hira", "Bilal", "Maryam"], last: ["K", "A", "H", "M", "R", "S", "I"] },
  "Bangladesh": { first: ["Rahim", "Rashida", "Karim", "Hasina", "Faruk", "Nasreen", "Jamal", "Sultana", "Habib", "Razia"], last: ["H", "R", "A", "K", "M", "I", "U"] },
  "Philippines": { first: ["Juan", "Maria", "Jose", "Ana", "Mark", "Jennifer", "John", "Michelle", "Michael", "Christine", "Ryan", "Angela"], last: ["S", "R", "D", "C", "G", "M", "L"] },
  "Indonesia": { first: ["Budi", "Siti", "Agus", "Dewi", "Eko", "Ratna", "Hendra", "Wati", "Dedi", "Yuni", "Rizky", "Putri"], last: ["S", "W", "P", "H", "R", "A", "K"] },
  "Vietnam": { first: ["Minh", "Linh", "Duc", "Huong", "Tuan", "Mai", "Long", "Ngoc", "Hung", "Lan", "Thanh", "Hoa"], last: ["N", "T", "L", "P", "H", "V", "D"] },
  "Thailand": { first: ["Somchai", "Suda", "Prasert", "Porn", "Wichai", "Nong", "Sompong", "Kaew", "Somsak", "Lek"], last: ["S", "P", "K", "T", "C", "W", "N"] },
  "Malaysia": { first: ["Ahmad", "Siti", "Muhammad", "Nur", "Mohd", "Fatimah", "Abdul", "Aminah", "Ismail", "Zainab"], last: ["A", "M", "I", "H", "R", "K", "S"] },
  "Singapore": { first: ["Wei Ming", "Hui Ling", "Jun Jie", "Xiu Mei", "Zhi Hao", "Mei Ling", "Jia Wei", "Shu Fen", "Kai Ming", "Li Hua"], last: ["T", "L", "C", "W", "N", "K", "O"] },
  "Japan": { first: ["Takeshi", "Yuki", "Hiroshi", "Sakura", "Kenji", "Aiko", "Satoshi", "Mika", "Daisuke", "Rina", "Yuto", "Hana"], last: ["S", "T", "K", "Y", "N", "M", "I"] },
  "South Korea": { first: ["Min-jun", "Ji-yeon", "Seo-jun", "Soo-yeon", "Jun-ho", "Min-ji", "Hyun-woo", "Hye-jin", "Sung-min", "Yuna"], last: ["K", "L", "P", "C", "J", "H", "S"] },
  "China": { first: ["Wei", "Fang", "Ming", "Xiu", "Jun", "Ying", "Lei", "Mei", "Cheng", "Hui", "Long", "Yan", "Jing", "Lin", "Tao", "Hong"], last: ["W", "L", "Z", "C", "H", "Y", "X", "G"] },
  
  // Europe
  "Germany": { first: ["Hans", "Anna", "Klaus", "Greta", "Stefan", "Ingrid", "Michael", "Sabine", "Thomas", "Ursula", "Markus", "Claudia"], last: ["M", "S", "W", "K", "B", "F", "H"] },
  "France": { first: ["Pierre", "Marie", "Jean", "Sophie", "Francois", "Claire", "Michel", "Isabelle", "Philippe", "Nathalie"], last: ["D", "M", "L", "B", "R", "G", "P"] },
  "Italy": { first: ["Marco", "Giulia", "Luca", "Francesca", "Alessandro", "Chiara", "Andrea", "Elena", "Matteo", "Sara"], last: ["R", "B", "M", "F", "C", "G", "D"] },
  "Spain": { first: ["Carlos", "Maria", "Pablo", "Elena", "Javier", "Carmen", "Miguel", "Ana", "Antonio", "Laura"], last: ["G", "R", "M", "F", "S", "L", "D"] },
  "Netherlands": { first: ["Jan", "Anna", "Pieter", "Sophie", "Willem", "Emma", "Thomas", "Lisa", "Dirk", "Sanne"], last: ["V", "D", "B", "J", "K", "S", "M"] },
  "Poland": { first: ["Piotr", "Anna", "Jakub", "Katarzyna", "Marcin", "Agnieszka", "Tomasz", "Magdalena", "Pawel", "Joanna"], last: ["K", "W", "N", "S", "M", "P", "Z"] },
  "Sweden": { first: ["Erik", "Anna", "Lars", "Karin", "Johan", "Ingrid", "Anders", "Maria", "Olof", "Sofia"], last: ["A", "S", "L", "N", "B", "E", "J"] },
  "Norway": { first: ["Lars", "Ingrid", "Erik", "Astrid", "Olav", "Karin", "Magnus", "Sigrid", "Harald", "Liv"], last: ["H", "N", "L", "B", "O", "S", "K"] },
  
  // UK & Commonwealth
  "United Kingdom": { first: ["James", "Emma", "Oliver", "Sophie", "William", "Charlotte", "Harry", "Grace", "George", "Lucy", "Thomas", "Emily"], last: ["S", "W", "J", "B", "T", "D", "H"] },
  "Australia": { first: ["Jack", "Emily", "Liam", "Olivia", "Noah", "Chloe", "Ethan", "Mia", "Mason", "Ava", "Lucas", "Isla"], last: ["S", "W", "J", "M", "T", "B", "K"] },
  "New Zealand": { first: ["Liam", "Olivia", "Noah", "Emma", "Oliver", "Ava", "William", "Sophia", "James", "Mia"], last: ["W", "S", "T", "B", "M", "J", "K"] },
  
  // USA & Americas
  "USA": { first: ["Michael", "Jennifer", "James", "Jessica", "John", "Ashley", "David", "Amanda", "Robert", "Sarah", "William", "Emily", "Christopher", "Samantha"], last: ["S", "J", "W", "B", "M", "D", "T"] },
  "Canada": { first: ["Liam", "Olivia", "Noah", "Emma", "Ethan", "Sophia", "James", "Ava", "Benjamin", "Isabella", "Lucas", "Mia"], last: ["S", "M", "B", "T", "L", "W", "C"] },
  "Brazil": { first: ["João", "Maria", "Pedro", "Ana", "Lucas", "Julia", "Gabriel", "Beatriz", "Matheus", "Larissa", "Rafael", "Fernanda"], last: ["S", "O", "L", "C", "F", "R", "M"] },
  "Mexico": { first: ["José", "Maria", "Juan", "Guadalupe", "Luis", "Rosa", "Carlos", "Ana", "Miguel", "Elena", "Francisco", "Carmen"], last: ["G", "R", "H", "M", "L", "S", "C"] },
  "Argentina": { first: ["Mateo", "Sofia", "Santiago", "Valentina", "Lucas", "Martina", "Thiago", "Camila", "Benjamin", "Isabella"], last: ["G", "R", "M", "F", "L", "S", "D"] },
  "Colombia": { first: ["Santiago", "Valentina", "Samuel", "Sofia", "Nicolas", "Isabella", "Alejandro", "Mariana", "Daniel", "Gabriela"], last: ["R", "G", "M", "L", "H", "C", "S"] },
  
  // Middle East
  "UAE": { first: ["Mohammed", "Fatima", "Ahmed", "Aisha", "Khalid", "Mariam", "Omar", "Sara", "Ali", "Noura", "Hassan", "Latifa"], last: ["A", "M", "K", "R", "S", "H", "N"] },
  "Saudi Arabia": { first: ["Abdullah", "Nora", "Mohammed", "Sara", "Faisal", "Lama", "Khaled", "Haya", "Salman", "Reem"], last: ["A", "S", "M", "K", "Q", "H", "B"] },
  "Qatar": { first: ["Hamad", "Moza", "Khalid", "Aisha", "Tamim", "Fatima", "Jassim", "Mariam", "Nasser", "Noura"], last: ["A", "T", "M", "H", "K", "N", "Q"] },
  "Turkey": { first: ["Mehmet", "Zeynep", "Mustafa", "Elif", "Ahmet", "Ayse", "Ali", "Fatma", "Hasan", "Emine", "Murat", "Hacer"], last: ["Y", "K", "O", "A", "S", "D", "C"] },
  "Lebanon": { first: ["Khalil", "Nadia", "Hassan", "Layla", "Omar", "Rania", "Ahmad", "Maya", "Samir", "Dina"], last: ["H", "K", "N", "S", "A", "M", "R"] }
};

const countryFlags = {
  "Nigeria": "🇳🇬", "Ghana": "🇬🇭", "Kenya": "🇰🇪", "South Africa": "🇿🇦",
  "Senegal": "🇸🇳", "Cameroon": "🇨🇲", "Tanzania": "🇹🇿", "Uganda": "🇺🇬",
  "Zimbabwe": "🇿🇼", "Ethiopia": "🇪🇹", "India": "🇮🇳", "Pakistan": "🇵🇰",
  "Bangladesh": "🇧🇩", "Philippines": "🇵🇭", "Indonesia": "🇮🇩", "Vietnam": "🇻🇳",
  "Thailand": "🇹🇭", "Malaysia": "🇲🇾", "Singapore": "🇸🇬", "Japan": "🇯🇵",
  "South Korea": "🇰🇷", "China": "🇨🇳", "Germany": "🇩🇪", "France": "🇫🇷",
  "Italy": "🇮🇹", "Spain": "🇪🇸", "Netherlands": "🇳🇱", "Poland": "🇵🇱",
  "Sweden": "🇸🇪", "Norway": "🇳🇴", "United Kingdom": "🇬🇧", "Australia": "🇦🇺",
  "New Zealand": "🇳🇿", "USA": "🇺🇸", "Canada": "🇨🇦", "Brazil": "🇧🇷",
  "Mexico": "🇲🇽", "Argentina": "🇦🇷", "Colombia": "🇨🇴", "UAE": "🇦🇪",
  "Saudi Arabia": "🇸🇦", "Qatar": "🇶🇦", "Turkey": "🇹🇷", "Lebanon": "🇱🇧"
};

// Random name generator for purchase notifications - now with proper matching
const generateRandomBuyer = () => {
  // Countries with their regions
  const countries = [
    // Africa
    { name: "Nigeria", region: "africa", flag: "🇳🇬" },
    { name: "Ghana", region: "africa", flag: "🇬🇭" },
    { name: "Kenya", region: "africa", flag: "🇰🇪" },
    { name: "South Africa", region: "africa", flag: "🇿🇦" },
    { name: "Senegal", region: "africa", flag: "🇸🇳" },
    { name: "Cameroon", region: "africa", flag: "🇨🇲" },
    { name: "Tanzania", region: "africa", flag: "🇹🇿" },
    { name: "Uganda", region: "africa", flag: "🇺🇬" },
    { name: "Zimbabwe", region: "africa", flag: "🇿🇼" },
    { name: "Ethiopia", region: "africa", flag: "🇪🇹" },
    // Asia
    { name: "India", region: "asia", flag: "🇮🇳" },
    { name: "Pakistan", region: "asia", flag: "🇵🇰" },
    { name: "Bangladesh", region: "asia", flag: "🇧🇩" },
    { name: "Philippines", region: "asia", flag: "🇵🇭" },
    { name: "Indonesia", region: "asia", flag: "🇮🇩" },
    { name: "Vietnam", region: "asia", flag: "🇻🇳" },
    { name: "Thailand", region: "asia", flag: "🇹🇭" },
    { name: "Malaysia", region: "asia", flag: "🇲🇾" },
    { name: "Singapore", region: "asia", flag: "🇸🇬" },
    { name: "Japan", region: "asia", flag: "🇯🇵" },
    { name: "South Korea", region: "asia", flag: "🇰🇷" },
    { name: "China", region: "asia", flag: "🇨🇳" },
    // Europe
    { name: "Germany", region: "europe", flag: "🇩🇪" },
    { name: "France", region: "europe", flag: "🇫🇷" },
    { name: "Italy", region: "europe", flag: "🇮🇹" },
    { name: "Spain", region: "europe", flag: "🇪🇸" },
    { name: "Netherlands", region: "europe", flag: "🇳🇱" },
    { name: "Poland", region: "europe", flag: "🇵🇱" },
    { name: "Sweden", region: "europe", flag: "🇸🇪" },
    { name: "Norway", region: "europe", flag: "🇳🇴" },
    // UK
    { name: "United Kingdom", region: "uk", flag: "🇬🇧" },
    { name: "Australia", region: "uk", flag: "🇦🇺" },
    { name: "New Zealand", region: "uk", flag: "🇳🇿" },
    // Americas
    { name: "USA", region: "usa", flag: "🇺🇸" },
    { name: "Canada", region: "usa", flag: "🇨🇦" },
    { name: "Brazil", region: "latam", flag: "🇧🇷" },
    { name: "Mexico", region: "latam", flag: "🇲🇽" },
    { name: "Argentina", region: "latam", flag: "🇦🇷" },
    { name: "Colombia", region: "latam", flag: "🇨🇴" },
    // Middle East
    { name: "UAE", region: "middleeast", flag: "🇦🇪" },
    { name: "Saudi Arabia", region: "middleeast", flag: "🇸🇦" },
    { name: "Qatar", region: "middleeast", flag: "🇶🇦" },
    { name: "Turkey", region: "middleeast", flag: "🇹🇷" },
    { name: "Lebanon", region: "middleeast", flag: "🇱🇧" }
  ];

  // Account types for notifications
  const accountTypes = [
    "Fresh Approved AdSense Account",
    "Fully Verified AdSense Account",
    "Aged Payment Received AdSense Account",
    "Fresh Approved AdMob Account",
    "Fully Verified AdMob Account",
    "Aged Payment Received AdMob Account"
  ];

  // Time ago options
  const timeAgo = [
    "Just now", "1 min ago", "2 mins ago", "3 mins ago", "5 mins ago",
    "8 mins ago", "10 mins ago", "12 mins ago", "15 mins ago"
  ];

  // Random selection
  const country = countries[Math.floor(Math.random() * countries.length)];
  
  // Get country-specific names or fallback
  const names = countryNames[country.name] || { first: ["Customer"], last: ["A", "B", "C"] };
  const firstName = names.first[Math.floor(Math.random() * names.first.length)];
  const lastInitial = names.last[Math.floor(Math.random() * names.last.length)];
  
  // Get region-specific avatar
  const avatars = regionAvatars[country.region] || regionAvatars.usa;
  const avatar = avatars[Math.floor(Math.random() * avatars.length)];
  
  const accountType = accountTypes[Math.floor(Math.random() * accountTypes.length)];
  const time = timeAgo[Math.floor(Math.random() * timeAgo.length)];

  return {
    name: `${firstName} ${lastInitial}.`,
    action: `just purchased ${accountType}`,
    location: country.name,
    flag: country.flag,
    time: time,
    avatar: avatar
  };
};

// Purchase notification popup component
const PurchaseNotification = ({ notification, onClose }) => {
  if (!notification) return null;
  
  return (
    <div 
      className="purchase-notification animate-slide-in"
      data-testid="purchase-notification"
    >
      <div className="notification-card relative">
        <button 
          onClick={onClose}
          className="absolute top-3 right-3 text-[#52525B] hover:text-white transition-colors p-1"
          data-testid="close-notification"
        >
          <X size={16} />
        </button>
        <div className="flex items-center gap-3">
          <img 
            src={notification.avatar} 
            alt={notification.name}
            className="w-11 h-11 rounded-full object-cover border-2 border-[#00E676] flex-shrink-0"
            loading="lazy"
          />
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm flex items-center gap-2 truncate">
              {notification.name} <span>{notification.flag}</span>
            </p>
            <p className="text-[#00E676] text-xs truncate">{notification.action}</p>
            <p className="text-[#52525B] text-xs flex items-center gap-1 mt-1">
              <MapPin size={10} className="flex-shrink-0" />
              <span className="truncate">{notification.location} • {notification.time}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Urgency Banner Component
const UrgencyBanner = React.memo(() => {
  return (
    <div className="urgency-banner" data-testid="urgency-banner">
      <div className="flex items-center justify-center gap-2 text-sm">
        <Flame size={16} className="text-orange-400 animate-pulse" />
        <span className="font-semibold">HIGH DEMAND</span>
        <span className="hidden sm:inline">—</span>
        <span>Limited Accounts Available Today!</span>
        <Flame size={16} className="text-orange-400 animate-pulse" />
      </div>
    </div>
  );
});

// Sticky CTA Button Component
const StickyCTA = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // Show sticky button after scrolling 300px
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!isVisible) return null;

  return (
    <a
      href={TELEGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleTelegramClick}
      className="sticky-cta"
      data-testid="sticky-cta"
    >
      <MessageCircle size={24} />
      <span>Join Our Telegram</span>
    </a>
  );
};

// Exit Intent Popup Component
const ExitIntentPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="exit-popup-overlay" data-testid="exit-popup">
      <div className="exit-popup">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-[#52525B] hover:text-white transition-colors"
          data-testid="close-exit-popup"
        >
          <X size={24} />
        </button>
        
        <div className="text-center">
          <div className="w-20 h-20 bg-[#00E676]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Gift size={40} className="text-[#00E676]" />
          </div>
          
          <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Wait! Don't Miss Out!
          </h3>
          
          <p className="text-[#A1A1AA] mb-6 max-w-sm mx-auto">
            Join our Telegram with <span className="text-[#00E676] font-bold">90,000+ members</span> to see available accounts, pricing, and get <span className="text-[#FFAB00] font-bold">exclusive deals</span>!
          </p>
          
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleTelegramClick}
            className="cta-telegram pulse-glow inline-flex mb-4"
            data-testid="exit-popup-cta"
          >
            <MessageCircle size={22} />
            Join Our Telegram
            <ArrowRight size={18} />
          </a>
          
          <p className="text-xs text-[#52525B]">
            Explore first • No pressure • 24/7 Support
          </p>
        </div>
      </div>
    </div>
  );
};

// FAQ Item Component
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div 
      className="faq-item"
      data-testid="faq-item"
    >
      <button 
        onClick={onClick}
        className="faq-question"
      >
        <span>{question}</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>
      {isOpen && (
        <div className="faq-answer">
          {answer}
        </div>
      )}
    </div>
  );
};

// Animated Stat Component - counts up when scrolled into view
const AnimatedStat = ({ number, label }) => {
  const [displayValue, setDisplayValue] = useState("0");
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasAnimated]);

  useEffect(() => {
    if (!hasAnimated) return;
    const match = number.match(/^(\d+)/);
    if (!match) {
      setDisplayValue(number);
      return;
    }
    const target = parseInt(match[1]);
    const suffix = number.slice(match[1].length);
    const duration = 2000;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplayValue(`${Math.floor(eased * target)}${suffix}`);
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [hasAnimated, number]);

  return (
    <div ref={ref} className="text-center fade-in-up" data-testid={`animated-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`mono text-4xl lg:text-5xl font-bold text-[#00E676] mb-2 ${hasAnimated ? 'stat-glow' : ''}`}>
        {hasAnimated ? displayValue : "0"}
      </div>
      <div className="text-[#A1A1AA]">{label}</div>
    </div>
  );
};

// Trust Seals Bar
const TrustSealsBar = React.memo(() => {
  const seals = [
    { icon: ShieldCheck, text: "10+ Years Trusted" },
    { icon: Award, text: "5000+ Delivered" },
    { icon: Zap, text: "Instant Transfer" },
    { icon: Clock, text: "24/7 Support" },
    { icon: CheckCircle, text: "Satisfaction Guaranteed" }
  ];

  return (
    <div className="trust-seals-section" data-testid="trust-seals">
      <div className="section-container">
        <div className="trust-seals-bar">
          {seals.map((seal, index) => (
            <div key={index} className="trust-seal" data-testid={`trust-seal-${index}`}>
              <div className="trust-seal-icon">
                <seal.icon size={18} />
              </div>
              <span className="trust-seal-text">{seal.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

// Scrolling "Recently Joined" Ticker
const JoinedTicker = () => {
  const tickerItems = useMemo(() => {
    const items = [];
    const countries = Object.keys(countryNames);
    for (let i = 0; i < 20; i++) {
      const country = countries[Math.floor(Math.random() * countries.length)];
      const names = countryNames[country];
      const firstName = names.first[Math.floor(Math.random() * names.first.length)];
      const flag = countryFlags[country] || "";
      const mins = Math.floor(Math.random() * 55) + 2;
      items.push({ name: firstName, flag, time: `${mins}m ago` });
    }
    return items;
  }, []);

  return (
    <div className="joined-ticker" data-testid="joined-ticker">
      <div className="joined-ticker-track">
        <div className="joined-ticker-items">
          {[...tickerItems, ...tickerItems].map((person, index) => (
            <div key={index} className="joined-ticker-item">
              <div className="joined-dot"></div>
              <span className="joined-flag">{person.flag}</span>
              <span className="joined-name">{person.name}</span>
              <span>joined Telegram</span>
              <span style={{ color: '#52525B' }}>{person.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Country Availability Showcase
const CountryShowcase = React.memo(() => {
  const countries = [
    { name: "USA", flag: "🇺🇸" }, { name: "UK", flag: "🇬🇧" },
    { name: "Canada", flag: "🇨🇦" }, { name: "Australia", flag: "🇦🇺" },
    { name: "Germany", flag: "🇩🇪" }, { name: "France", flag: "🇫🇷" },
    { name: "Italy", flag: "🇮🇹" }, { name: "Spain", flag: "🇪🇸" },
    { name: "Netherlands", flag: "🇳🇱" }, { name: "Sweden", flag: "🇸🇪" },
    { name: "Norway", flag: "🇳🇴" }, { name: "Poland", flag: "🇵🇱" },
    { name: "India", flag: "🇮🇳" }, { name: "Pakistan", flag: "🇵🇰" },
    { name: "Japan", flag: "🇯🇵" }, { name: "S. Korea", flag: "🇰🇷" },
    { name: "China", flag: "🇨🇳" }, { name: "Singapore", flag: "🇸🇬" },
    { name: "Malaysia", flag: "🇲🇾" }, { name: "Philippines", flag: "🇵🇭" },
    { name: "Indonesia", flag: "🇮🇩" }, { name: "Vietnam", flag: "🇻🇳" },
    { name: "Thailand", flag: "🇹🇭" }, { name: "Bangladesh", flag: "🇧🇩" },
    { name: "Nigeria", flag: "🇳🇬" }, { name: "Ghana", flag: "🇬🇭" },
    { name: "Kenya", flag: "🇰🇪" }, { name: "S. Africa", flag: "🇿🇦" },
    { name: "Senegal", flag: "🇸🇳" }, { name: "Cameroon", flag: "🇨🇲" },
    { name: "Tanzania", flag: "🇹🇿" }, { name: "Uganda", flag: "🇺🇬" },
    { name: "Ethiopia", flag: "🇪🇹" }, { name: "Zimbabwe", flag: "🇿🇼" },
    { name: "UAE", flag: "🇦🇪" }, { name: "Saudi", flag: "🇸🇦" },
    { name: "Qatar", flag: "🇶🇦" }, { name: "Turkey", flag: "🇹🇷" },
    { name: "Lebanon", flag: "🇱🇧" }, { name: "Brazil", flag: "🇧🇷" },
    { name: "Mexico", flag: "🇲🇽" }, { name: "Argentina", flag: "🇦🇷" },
    { name: "Colombia", flag: "🇨🇴" }, { name: "N. Zealand", flag: "🇳🇿" },
  ];

  return (
    <section id="countries" className="py-20 lg:py-32" data-testid="country-showcase">
      <div className="section-container">
        <div className="text-center mb-12 fade-in-up">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Buy AdSense Accounts — <span className="text-[#00E676]">All Countries Available</span>
          </h2>
          <p className="text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto">
            Google AdSense and AdMob accounts for sale worldwide. USA, UK, Nigeria, India, Canada, Australia, and 40+ more countries.
          </p>
        </div>

        <div className="country-grid">
          {countries.map((country, index) => (
            <div key={index} className="country-chip" data-testid={`country-${index}`}>
              <span className="country-flag">{country.flag}</span>
              <span className="country-name">{country.name}</span>
              <div className="available-dot"></div>
            </div>
          ))}
        </div>

        <p className="text-center text-sm text-[#52525B] mt-8">
          Don't see your country? We sell AdSense and AdMob accounts for <span className="text-[#00E676]">every country</span> worldwide. Contact us on Telegram.
        </p>
      </div>
    </section>
  );
});

// Why Join Our Telegram Section
const WhyJoinTelegram = React.memo(() => {
  const reasons = [
    {
      icon: Users,
      color: "#29B6F6",
      bg: "rgba(41, 182, 246, 0.1)",
      title: "90,000+ Active Members",
      description: "Join a thriving community of publishers and app developers from around the world."
    },
    {
      icon: Globe,
      color: "#00E676",
      bg: "rgba(0, 230, 118, 0.1)",
      title: "See Live Available Stock",
      description: "Browse real-time listings of AdSense & AdMob accounts with country and type details."
    },
    {
      icon: Banknote,
      color: "#FFAB00",
      bg: "rgba(255, 171, 0, 0.1)",
      title: "Get Pricing Instantly",
      description: "No hidden fees. Message us your country and account type — get a quote in minutes."
    },
    {
      icon: Star,
      color: "#E040FB",
      bg: "rgba(224, 64, 251, 0.1)",
      title: "Read Real Member Reviews",
      description: "See verified feedback from 5000+ customers who already purchased accounts."
    },
    {
      icon: Gift,
      color: "#FF6B35",
      bg: "rgba(255, 107, 53, 0.1)",
      title: "Exclusive Deals & Discounts",
      description: "Members get first access to special offers, bulk discounts, and flash sales."
    },
    {
      icon: MessageCircle,
      color: "#29B6F6",
      bg: "rgba(41, 182, 246, 0.1)",
      title: "Chat With Our Team 24/7",
      description: "Ask any question before you buy. Our team is available around the clock."
    }
  ];

  return (
    <section className="py-20 lg:py-32" data-testid="why-join-section">
      <div className="section-container">
        <div className="text-center mb-16 fade-in-up">
          <div className="inline-flex items-center gap-2 trust-badge mb-6">
            <MessageCircle size={16} />
            <span>Free to Join - No Obligations</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="why-join-headline">
            Why Join Our <span className="text-[#29B6F6]">Telegram?</span>
          </h2>
          <p className="text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto">
            Explore first, decide later. Here's what you'll find inside our community.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="why-join-card fade-in-up"
              style={{ animationDelay: `${index * 0.08}s` }}
              data-testid={`why-join-${index}`}
            >
              <div className="why-join-icon" style={{ background: reason.bg }}>
                <reason.icon size={22} style={{ color: reason.color }} />
              </div>
              <div>
                <h3 className="text-white font-semibold mb-1.5 text-sm sm:text-base">{reason.title}</h3>
                <p className="text-[#71717A] text-xs sm:text-sm leading-relaxed">{reason.description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center mt-12">
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleTelegramClick}
            className="cta-telegram pulse-glow inline-flex"
            data-testid="why-join-cta"
          >
            <MessageCircle size={22} />
            Join Our Telegram
            <ArrowRight size={18} />
          </a>
          <p className="text-xs text-[#52525B] mt-4">Explore first - No pressure - 24/7 Support</p>
        </div>
      </div>
    </section>
  );
});

const LandingPage = () => {
  const [currentNotification, setCurrentNotification] = useState(null);
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [hasShownExitPopup, setHasShownExitPopup] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);

  // Remove static hero once React is ready
  useEffect(() => {
    const staticHero = document.getElementById('static-hero');
    if (staticHero) {
      staticHero.style.transition = 'opacity 0.2s ease-out';
      staticHero.style.opacity = '0';
      setTimeout(() => { if (staticHero.parentNode) staticHero.parentNode.removeChild(staticHero); }, 250);
    }
  }, []);

  // Exit intent detection
  useEffect(() => {
    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !hasShownExitPopup) {
        setShowExitPopup(true);
        setHasShownExitPopup(true);
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [hasShownExitPopup]);

  // FAQ Data
  const faqData = [
    {
      question: "What's the difference between AdSense and AdMob?",
      answer: "Google AdSense is for monetizing websites and blogs with display ads. Google AdMob is for monetizing mobile apps (Android/iOS) with in-app ads. We sell verified accounts for both platforms!"
    },
    {
      question: "Is it safe to buy AdSense/AdMob accounts from you?",
      answer: "Absolutely! We've been in this business for over 10 years with 5000+ successful transactions. All our accounts are thoroughly verified, have clean history, and come with full ownership transfer. We provide complete login credentials and ongoing support."
    },
    {
      question: "What do I receive after purchase?",
      answer: "For AdSense: You get AdSense login, WordPress admin access, and domain login. For AdMob: You get AdMob login and app source files/access. Full ownership and control is transferred to you."
    },
    {
      question: "How long does the transfer process take?",
      answer: "Most transfers are completed within 1-24 hours after payment confirmation. We work around the clock to ensure fast delivery. Complex accounts may take up to 48 hours."
    },
    {
      question: "Which countries are available?",
      answer: "We have AdSense and AdMob accounts from 50+ countries including USA, UK, Canada, Australia, Germany, France, and many more. Just tell us your preferred country and we'll check availability and provide pricing."
    },
    {
      question: "What's the difference between Fresh, Verified, and Aged accounts?",
      answer: "Fresh Approved: Newly approved accounts ready for monetization. Fully Verified: PIN verified with payment method setup. Aged Payment Received: Established accounts with payment history - most trusted and best for high earnings. All types available for both AdSense and AdMob."
    },
    {
      question: "Do you offer any guarantee or support?",
      answer: "Yes! We provide 24/7 support via Telegram. If you face any issues within the first 30 days, we'll help resolve them or provide a replacement. Your satisfaction is our priority."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept various payment methods including cryptocurrency (Bitcoin, USDT), bank transfer, and other popular payment options. Contact us on Telegram for available payment methods in your region."
    }
  ];

  // Remove Emergent badge
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.parentNode.removeChild(badge);
      }
      // Also try removing by href
      document.querySelectorAll('a[href*="emergent.sh"]').forEach(el => {
        if (el.textContent.includes('Made with') || el.id === 'emergent-badge') {
          el.parentNode.removeChild(el);
        }
      });
    };
    
    removeBadge();
    const t1 = setTimeout(removeBadge, 0);
    const t2 = setTimeout(removeBadge, 50);
    const t3 = setTimeout(removeBadge, 100);
    const t4 = setTimeout(removeBadge, 200);
    const t5 = setTimeout(removeBadge, 500);
    const t6 = setTimeout(removeBadge, 1000);
    
    // Watch for re-injection
    const observer = new MutationObserver(removeBadge);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      clearTimeout(t6);
      observer.disconnect();
    };
  }, []);

  // Show notifications periodically with random buyers
  useEffect(() => {
    const showNotification = () => {
      const randomBuyer = generateRandomBuyer();
      setCurrentNotification(randomBuyer);
      setTimeout(() => {
        setCurrentNotification(null);
      }, 5000);
    };

    // Show first notification after 2 seconds
    const initialTimeout = setTimeout(showNotification, 2000);
    
    // Show subsequent notifications every 8 seconds
    const interval = setInterval(showNotification, 8000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, []);

  // Testimonials data
  const testimonials = [
    {
      name: "David Okonkwo",
      country: "Nigeria",
      flag: "🇳🇬",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Aged Payment Received",
      text: "I've been scammed twice before trying to buy AdSense accounts. Found these guys through a friend and they delivered exactly what they promised. My account has been running for 8 months now with no issues. Finally, a trusted seller!",
      date: "November 2024"
    },
    {
      name: "Sarah Mitchell",
      country: "USA",
      flag: "🇺🇸",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fully Verified Account",
      text: "Best decision I made for my blog network. The account came with full WordPress access and domain logins as promised. Customer support on Telegram was responsive 24/7. Highly recommend to anyone serious about monetization.",
      date: "October 2024"
    },
    {
      name: "Rajesh Patel",
      country: "India",
      flag: "🇮🇳",
      avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fresh Approved Account",
      text: "Was skeptical at first but they have been in business for over 10 years. The fresh approved account I bought started earning within the first week. Very professional service and smooth transfer process.",
      date: "December 2024"
    },
    {
      name: "Emma Thompson",
      country: "United Kingdom",
      flag: "🇬🇧",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Aged Payment Received",
      text: "Third time purchasing from them. The aged accounts with payment history are gold! Each account I bought has been performing excellently. These guys are the real deal in this industry.",
      date: "September 2024"
    },
    {
      name: "Kwame Asante",
      country: "Ghana",
      flag: "🇬🇭",
      avatar: "https://images.unsplash.com/photo-1463453091185-61582044d556?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fully Verified Account",
      text: "After struggling for 2 years to get AdSense approval, I decided to buy. The fully verified account came with everything - AdSense, WordPress, and domain access. Making money online is now a reality for me!",
      date: "November 2024"
    },
    {
      name: "Liu Wei",
      country: "Singapore",
      flag: "🇸🇬",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fresh Approved Account",
      text: "Running an agency and needed multiple accounts. They provided excellent bulk pricing and all accounts have been working perfectly for 6+ months. Reliable and trustworthy - exactly what this market needs.",
      date: "August 2024"
    },
    {
      name: "Fatima Al-Hassan",
      country: "UAE",
      flag: "🇦🇪",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Aged Payment Received",
      text: "The team has been incredibly helpful throughout the process. Got an aged account with payment history from the USA. The transfer was seamless and I received my first payment within 30 days!",
      date: "October 2024"
    },
    {
      name: "Hans Mueller",
      country: "Germany",
      flag: "🇩🇪",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fully Verified Account",
      text: "Very impressed with the quality of service. The account was exactly as described - fully verified with clean history. They even helped me set up everything properly. 10/10 would recommend!",
      date: "December 2024"
    },
    {
      name: "Amara Diallo",
      country: "Senegal",
      flag: "🇸🇳",
      avatar: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fresh Approved Account",
      text: "Finally found a legitimate seller after months of searching. The fresh approved account is performing great. Their 10+ years of experience really shows in how professionally they handle everything.",
      date: "November 2024"
    },
    {
      name: "John Anderson",
      country: "Australia",
      flag: "🇦🇺",
      avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Aged Payment Received",
      text: "Been in the digital marketing space for years and these guys are the most reliable AdSense account providers I've worked with. Quick delivery, genuine accounts, and excellent after-sale support.",
      date: "September 2024"
    },
    {
      name: "Yuki Tanaka",
      country: "Japan",
      flag: "🇯🇵",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fully Verified Account",
      text: "Purchased for my tech blog network. The account quality is excellent and the WordPress setup was already optimized. Very happy with the investment. Will definitely buy more accounts soon.",
      date: "October 2024"
    },
    {
      name: "Carlos Rodriguez",
      country: "Spain",
      flag: "🇪🇸",
      avatar: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=80&h=80&fit=crop&crop=face",
      rating: 5,
      type: "Fresh Approved Account",
      text: "Excellent service from start to finish. The communication on Telegram was fast and professional. Got my account within hours of payment. These guys truly care about their customers.",
      date: "December 2024"
    }
  ];
  const accountTypes = [
    {
      icon: Zap,
      title: "Fresh Approved",
      description: "Brand new Google AdSense & AdMob accounts, freshly approved and ready for monetization.",
      tags: ["AdSense", "AdMob"]
    },
    {
      icon: ShieldCheck,
      title: "Fully Verified",
      description: "Completely verified accounts with PIN confirmation. Ready to receive payments immediately.",
      tags: ["AdSense", "AdMob"]
    },
    {
      icon: Banknote,
      title: "Aged Payment Received",
      description: "Established accounts with payment history. Proven track record of receiving payments.",
      tags: ["AdSense", "AdMob"]
    }
  ];

  const whatYouGet = [
    {
      icon: Award,
      title: "Google AdSense Logins",
      description: "Full access to AdSense dashboard for website monetization."
    },
    {
      icon: Globe,
      title: "Google AdMob Logins",
      description: "Full access to AdMob dashboard for mobile app monetization."
    },
    {
      icon: CheckCircle,
      title: "WordPress / App Access",
      description: "Complete access to your website or app source files."
    }
  ];

  const stats = [
    { number: "5000+", label: "Accounts Sold" },
    { number: "10+", label: "Years Experience" },
    { number: "50+", label: "Countries" },
    { number: "24/7", label: "Support" }
  ];

  return (
    <div className="landing-page" data-testid="landing-page">
      {/* Urgency Banner */}
      <UrgencyBanner />
      
      {/* Exit Intent Popup */}
      <ExitIntentPopup 
        isOpen={showExitPopup} 
        onClose={() => setShowExitPopup(false)} 
      />
      
      {/* Sticky CTA Button */}
      <StickyCTA />
      
      {/* Google Translate Widget - Small manual override */}
      <div id="google_translate_element" className="fixed bottom-3 left-3 z-30 opacity-50 hover:opacity-100 transition-opacity scale-75 origin-bottom-left" data-testid="translate-widget"></div>
      
      {/* Purchase Notification Popup */}
      <PurchaseNotification 
        notification={currentNotification} 
        onClose={() => setCurrentNotification(null)} 
      />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-glow"></div>
        
        {/* Decorative circles */}
        <div className="decoration-circle" style={{ width: '600px', height: '600px', top: '-200px', right: '-200px', opacity: 0.5 }}></div>
        <div className="decoration-circle" style={{ width: '400px', height: '400px', bottom: '-100px', left: '-100px', opacity: 0.3 }}></div>
        
        <div className="section-container w-full">
          <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16 xl:gap-20">
            {/* Left Content */}
            <div className="flex-1 text-center lg:text-left fade-in-up px-2 sm:px-0">
              {/* Trust Badge */}
              <div className="inline-flex items-center gap-2 trust-badge mb-4 sm:mb-6 text-xs sm:text-sm" data-testid="trust-badge">
                <ShieldCheck size={14} className="flex-shrink-0" />
                <span>5000+ Accounts Sold • All Countries Available</span>
              </div>
              
              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 sm:mb-6" data-testid="hero-headline">
                <span className="text-white">Buy Verified Google</span>
                <br />
                <span className="gradient-text">AdSense & AdMob Accounts</span>
              </h1>
              
              {/* Subheadline */}
              <p className="text-sm sm:text-base lg:text-lg text-[#A1A1AA] mb-6 sm:mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed" data-testid="hero-subheadline">
                Fresh Approved · Fully Verified · Aged with Payment History
                <br className="hidden sm:block" />
                <span className="sm:hidden"> — </span>
                Buy AdSense accounts for websites • Buy AdMob accounts for mobile apps
              </p>
              
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start mb-8 sm:mb-0">
                <a 
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTelegramClick}
                  className="cta-telegram pulse-glow"
                  data-testid="hero-cta-telegram"
                >
                  <MessageCircle size={20} className="flex-shrink-0" />
                  <span>Join Our Telegram</span>
                  <ArrowRight size={16} className="flex-shrink-0" />
                </a>
                <a 
                  href={TELEGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleTelegramClick}
                  className="cta-primary"
                  data-testid="hero-cta-primary"
                >
                  See Available Accounts
                </a>
              </div>
              
              {/* Quick Stats */}
              <div className="flex flex-wrap gap-4 sm:gap-6 mt-6 sm:mt-10 justify-center lg:justify-start">
                {stats.slice(0, 3).map((stat, index) => (
                  <div key={index} className="stat-item" data-testid={`stat-${index}`}>
                    <div className="stat-number text-2xl sm:text-3xl">{stat.number}</div>
                    <div className="stat-label text-xs sm:text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Visual - Hidden on very small screens, shown on sm+ */}
            <div className="flex-1 relative fade-in-up delay-200 hidden sm:block" data-testid="hero-visual">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#00E676]/20 to-transparent rounded-2xl sm:rounded-3xl blur-3xl"></div>
                <div className="glass border border-white/10 rounded-2xl sm:rounded-3xl p-6 sm:p-8 relative">
                  <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-[#00E676]/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Award className="text-[#00E676]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white text-sm sm:text-base">Premium Accounts</h3>
                      <p className="text-xs sm:text-sm text-[#A1A1AA]">AdSense & AdMob Ready</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-3 text-xs sm:text-sm">
                      <CheckCircle className="text-[#00E676] flex-shrink-0" size={16} />
                      <span className="text-[#A1A1AA]">Google AdSense Logins</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs sm:text-sm">
                      <CheckCircle className="text-[#00E676] flex-shrink-0" size={16} />
                      <span className="text-[#A1A1AA]">Google AdMob Logins</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs sm:text-sm">
                      <CheckCircle className="text-[#00E676] flex-shrink-0" size={16} />
                      <span className="text-[#A1A1AA]">WordPress / App Logins</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs sm:text-sm">
                      <CheckCircle className="text-[#00E676] flex-shrink-0" size={16} />
                      <span className="text-[#A1A1AA]">Full Ownership Transfer</span>
                    </div>
                  </div>
                  
                  <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/10">
                    <div className="flex items-center justify-between">
                      <span className="text-[#A1A1AA] text-xs sm:text-sm">Any Country</span>
                      <span className="mono text-[#00E676] font-semibold text-sm sm:text-base">Available</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Trust Seals Bar */}
      <TrustSealsBar />
      
      {/* Recently Joined Ticker */}
      <JoinedTicker />
      
      {/* Account Types Section */}
      <section id="accounts" className="py-20 lg:py-32 relative" data-testid="account-types-section">
        <div className="section-container">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="account-types-headline">
              Buy AdSense & AdMob — <span className="text-[#00E676]">Choose Your Account Type</span>
            </h2>
            <p className="text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto">
              Verified Google AdSense and AdMob accounts for sale. Available for all countries. Tell us your country and we'll provide the price!
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {accountTypes.map((type, index) => (
              <div 
                key={index} 
                className="feature-card fade-in-up text-center"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`account-type-${index}`}
              >
                <div className="w-16 h-16 bg-[#00E676]/10 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <type.icon className="text-[#00E676]" size={32} />
                </div>
                <div className="flex justify-center gap-2 mb-3">
                  <span className="text-xs bg-[#00E676]/10 text-[#00E676] px-2 py-1 rounded-full">AdSense</span>
                  <span className="text-xs bg-[#29B6F6]/10 text-[#29B6F6] px-2 py-1 rounded-full">AdMob</span>
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{type.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 lg:py-32 bg-[#0A0F0B]" data-testid="what-you-get-section">
        <div className="section-container">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="what-you-get-headline">
              What You <span className="text-[#00E676]">Get From Us</span>
            </h2>
            <p className="text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto">
              Complete ownership transfer with all login credentials you need
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {whatYouGet.map((item, index) => (
              <div 
                key={index} 
                className="feature-card fade-in-up text-center"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`what-you-get-${index}`}
              >
                <div className="w-16 h-16 bg-[#00E676]/10 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <item.icon className="text-[#00E676]" size={32} />
                </div>
                <h3 className="text-xl font-semibold mb-3 text-white">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Country Availability Showcase */}
      <CountryShowcase />
      
      {/* Stats Section - Animated */}
      <section className="py-16 lg:py-24 bg-[#0A0F0B]" data-testid="stats-section">
        <div className="section-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <AnimatedStat key={index} number={stat.number} label={stat.label} />
            ))}
          </div>
        </div>
      </section>
      
      {/* How It Works Section */}
      <section className="py-20 lg:py-32" data-testid="how-it-works-section">
        <div className="section-container">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="how-it-works-headline">
              How It <span className="text-[#00E676]">Works</span>
            </h2>
            <p className="text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto">
              Get your AdSense or AdMob account in three simple steps
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Contact Us", description: "Message us on Telegram. Tell us: AdSense or AdMob? Which country?" },
              { step: "02", title: "Get Your Price", description: "We provide pricing based on account type and country" },
              { step: "03", title: "Receive Logins", description: "Get your account logins transferred securely to you" }
            ].map((item, index) => (
              <div key={index} className="text-center fade-in-up" style={{ animationDelay: `${index * 0.15}s` }} data-testid={`step-${index}`}>
                <div className="mono text-6xl lg:text-7xl font-bold text-[#00E676]/20 mb-4">{item.step}</div>
                <h3 className="text-xl font-semibold mb-2 text-white">{item.title}</h3>
                <p className="text-[#A1A1AA] text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Join Our Telegram */}
      <WhyJoinTelegram />

      {/* Testimonials Section */}
      <section id="reviews" className="py-20 lg:py-32 bg-[#0A0F0B]" data-testid="testimonials-section">
        <div className="section-container">
          <div className="text-center mb-16 fade-in-up">
            <div className="inline-flex items-center gap-2 trust-badge mb-6">
              <Award size={16} />
              <span>10+ Years Helping Customers Worldwide</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="testimonials-headline">
              Trusted AdSense Seller — <span className="text-[#00E676]">5000+ Happy Customers</span>
            </h2>
            <p className="text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto">
              Real verified reviews from customers who bought Google AdSense and AdMob accounts from us
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className="testimonial-card fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`testimonial-${index}`}
              >
                {/* Header */}
                <div className="flex items-center gap-4 mb-4">
                  <img 
                    src={testimonial.avatar} 
                    alt={`${testimonial.name} from ${testimonial.country} - Verified ${testimonial.type} buyer review`}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#00E676]/30"
                    loading="lazy"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-white">{testimonial.name}</h4>
                      <span className="text-lg">{testimonial.flag}</span>
                    </div>
                    <p className="text-xs text-[#A1A1AA]">{testimonial.country}</p>
                  </div>
                </div>
                
                {/* Rating */}
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={14} className="text-[#FFAB00] fill-[#FFAB00]" />
                  ))}
                  <span className="text-xs text-[#52525B] ml-2">{testimonial.type}</span>
                </div>
                
                {/* Text */}
                <p className="text-sm text-[#A1A1AA] leading-relaxed mb-4">"{testimonial.text}"</p>
                
                {/* Date */}
                <p className="text-xs text-[#52525B]">{testimonial.date}</p>
              </div>
            ))}
          </div>

          {/* Trust Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="fade-in-up">
              <div className="mono text-3xl lg:text-4xl font-bold text-[#00E676]">10+</div>
              <div className="text-sm text-[#A1A1AA]">Years in Business</div>
            </div>
            <div className="fade-in-up" style={{ animationDelay: '0.1s' }}>
              <div className="mono text-3xl lg:text-4xl font-bold text-[#00E676]">5000+</div>
              <div className="text-sm text-[#A1A1AA]">Happy Customers</div>
            </div>
            <div className="fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="mono text-3xl lg:text-4xl font-bold text-[#00E676]">50+</div>
              <div className="text-sm text-[#A1A1AA]">Countries Served</div>
            </div>
            <div className="fade-in-up" style={{ animationDelay: '0.3s' }}>
              <div className="mono text-3xl lg:text-4xl font-bold text-[#00E676]">99%</div>
              <div className="text-sm text-[#A1A1AA]">Satisfaction Rate</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 lg:py-32" data-testid="faq-section">
        <div className="section-container">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="faq-headline">
              Buying AdSense Accounts — <span className="text-[#00E676]">FAQ</span>
            </h2>
            <p className="text-base lg:text-lg text-[#A1A1AA] max-w-2xl mx-auto">
              Common questions about buying verified Google AdSense and AdMob accounts
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto">
            {faqData.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
              />
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="text-[#A1A1AA] mb-4">Still have questions?</p>
            <a 
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleTelegramClick}
              className="cta-primary inline-flex"
              data-testid="faq-cta"
            >
              <MessageCircle size={20} />
              Ask Us on Telegram
            </a>
          </div>
        </div>
      </section>
      
      {/* Final CTA Section */}
      <section className="py-20 lg:py-32 relative overflow-hidden" data-testid="final-cta-section">
        <div className="hero-glow"></div>
        <div className="section-container relative z-10">
          <div className="glass border border-white/10 rounded-3xl p-8 lg:p-16 text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 trust-badge mb-6">
              <Users size={16} />
              <span>90,000+ Telegram Members • Any Country Available</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="final-cta-headline">
              Buy Your AdSense Account <span className="text-[#00E676]">Today</span>
            </h2>
            
            <p className="text-base lg:text-lg text-[#A1A1AA] mb-8 max-w-xl mx-auto">
              Join our Telegram to browse available Google AdSense and AdMob accounts, get pricing, read customer reviews, and buy securely.
            </p>
            
            <a 
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleTelegramClick}
              className="cta-telegram pulse-glow inline-flex text-lg"
              data-testid="final-cta-button"
            >
              <MessageCircle size={24} />
              Join Our Telegram
              <ArrowRight size={20} />
            </a>
            
            <p className="text-sm text-[#52525B] mt-6">
              Explore first • No pressure • 24/7 Available
            </p>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <footer className="py-8 border-t border-white/5" data-testid="footer">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Banknote className="text-[#00E676]" size={24} />
              <span className="font-semibold text-white">AdSenseBuys — Buy Verified Google AdSense & AdMob Accounts</span>
            </div>
            <p className="text-sm text-[#52525B]">
              © 2014-2026 AdSenseBuys. Trusted AdSense & AdMob account marketplace. 5000+ accounts sold worldwide.
            </p>
            <a 
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleTelegramClick}
              className="flex items-center gap-2 text-[#29B6F6] hover:text-[#039BE5] transition-colors"
              data-testid="footer-telegram-link"
            >
              <MessageCircle size={18} />
              <span>@Googleadsenseearning</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/banners" element={<Suspense fallback={<div style={{background:'#020402',minHeight:'100vh'}}/>}><BannersPage /></Suspense>} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
